import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import {
  ApiConfiguration,
  AmoxcalliLoginResponseDto,
  AmoxcalliLoginResponseDtoApiResponse,
  AmoxcalliParentDtoApiResponse,
  apiAuthLoginPost$Json,
  apiAuthProfilePatch$Json,
  apiAuthChangePasswordPost,
} from 'amoxcalli-identity-api';

import { AuthActions } from './auth.actions';
import { AuthUser } from '../../models/user.model';
import { authFeature } from './auth.reducer';

const TOKEN_KEY   = 'amxp_token';
const REFRESH_KEY = 'amxp_refresh';
const USER_KEY    = 'amxp_user';

function mapToAuthUser(dto: AmoxcalliLoginResponseDto): AuthUser {
  return {
    id: dto.userId ?? '',
    email: dto.email ?? '',
    nombre: dto.name ?? '',
    apellidoPaterno: dto.lastName ?? '',
    apellidoMaterno: dto.motherLastName ?? undefined,
    role: 'padre',
  };
}

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly http     = inject(HttpClient);
  private readonly config   = inject(ApiConfiguration);
  private readonly router   = inject(Router);
  private readonly store    = inject(Store);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ email, password }) =>
        apiAuthLoginPost$Json(this.http, this.config.rootUrl, {
          body: { email, password, app: 1 },
        }).pipe(
          map((response) => {
            const wrapper = response.body as AmoxcalliLoginResponseDtoApiResponse;
            const dto = wrapper.data as AmoxcalliLoginResponseDto;
            const token = dto.accessToken ?? '';
            const refreshToken = dto.refreshToken ?? '';
            return AuthActions.loginSuccess({ user: mapToAuthUser(dto), token, refreshToken });
          }),
          catchError(() => of(AuthActions.loginFailure({ error: '' })))
        )
      )
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ token, refreshToken, user }) => {
          localStorage.setItem(TOKEN_KEY, token);
          localStorage.setItem(REFRESH_KEY, refreshToken);
          localStorage.setItem(USER_KEY, JSON.stringify(user));
          this.router.navigate(['/padre/dashboard']);
        })
      ),
    { dispatch: false }
  );

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.updateProfile),
      withLatestFrom(this.store.select(authFeature.selectUser)),
      switchMap(([{ nombre, apellidoPaterno, apellidoMaterno }, currentUser]) =>
        apiAuthProfilePatch$Json(this.http, this.config.rootUrl, {
          body: { name: nombre, lastName: apellidoPaterno, motherLastName: apellidoMaterno ?? null },
        }).pipe(
          map((response) => {
            const wrapper = response.body as AmoxcalliParentDtoApiResponse;
            const dto = wrapper.data;
            const user: AuthUser = {
              ...currentUser!,
              nombre:          dto?.name ?? nombre,
              apellidoPaterno: dto?.lastName ?? apellidoPaterno,
              apellidoMaterno: dto?.motherLastName ?? apellidoMaterno,
            };
            return AuthActions.updateProfileSuccess({ user });
          }),
          catchError(err =>
            of(AuthActions.updateProfileFailure({ error: err?.error?.message ?? 'Error al guardar el perfil' }))
          )
        )
      )
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      switchMap(({ currentPassword, newPassword }) =>
        apiAuthChangePasswordPost(this.http, this.config.rootUrl, {
          body: { currentPassword, newPassword },
        }).pipe(
          map(() => AuthActions.changePasswordSuccess()),
          catchError(err =>
            of(AuthActions.changePasswordFailure({ error: err?.error?.message ?? 'No se pudo cambiar la contraseña' }))
          )
        )
      )
    )
  );

  persistProfile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.updateProfileSuccess),
        tap(({ user }) => localStorage.setItem(USER_KEY, JSON.stringify(user)))
      ),
    { dispatch: false }
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_KEY);
          localStorage.removeItem(USER_KEY);
          this.router.navigate(['/auth/login']);
        })
      ),
    { dispatch: false }
  );
}
